import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="mb-8">
    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-700">
      {title}
    </h2>
    {children}
  </div>
)

const P = ({ children }: { children: React.ReactNode }) => (
  <p className="text-gray-700 dark:text-gray-300 mb-3 leading-relaxed">{children}</p>
)

const Highlight = ({ children }: { children: React.ReactNode }) => (
  <span className="font-semibold text-gray-900 dark:text-white">{children}</span>
)

const AttitudeRow = ({ name, pts, desc, color }: { name: string; pts: string; desc: string; color: 'green' | 'red' }) => {
  const [open, setOpen] = useState(false)
  const ptsColor = color === 'green' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
  const tipBg = color === 'green'
    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
    : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'

  return (
    <div className="py-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900 dark:text-white text-sm">{name}</span>
          <button
            onClick={() => setOpen(!open)}
            className="w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-xs flex items-center justify-center hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            title="Ver descrição"
          >
            ?
          </button>
        </div>
        <span className={`font-bold text-sm ${ptsColor}`}>{pts}</span>
      </div>
      {open && (
        <div className={`mt-2 p-3 rounded-btn border text-sm text-gray-700 dark:text-gray-300 leading-relaxed ${tipBg}`}>
          {desc}
        </div>
      )}
    </div>
  )
}

export const OwnershipRulesPage = () => {
  const navigate = useNavigate()

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Regras do Ownership
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Programa de Participação nos Resultados e Liderança — Grupo Jonnpo
          </p>
        </div>
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          Voltar
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6 md:p-8">

        {/* O que é */}
        <Section title="O que é o Ownership?">
          <P>
            O Ownership é o programa de participação nos resultados e liderança do Grupo Jonnpo. Ele avalia mensalmente a atitude de cada colaborador
            e converte isso em pontos que determinam sua participação na distribuição de lucros da empresa.
          </P>
          <P>
            A filosofia é simples: <Highlight>atitude gera ponto, ponto gera participação no resultado.</Highlight>
          </P>
        </Section>

        {/* Como fazer parte */}
        <Section title="Como fazer parte">
          <P>
            Para participar do Ownership, o colaborador precisa:
          </P>
          <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 mb-3">
            <li>Ter no mínimo <Highlight>1 ano de empresa</Highlight></li>
            <li>Ser aprovado pelo administrador como <Highlight>elegível ao Ownership</Highlight></li>
            <li>Estar vinculado a pelo menos uma empresa do grupo</li>
          </ul>
          <P>
            Uma vez elegível, o colaborador passa a ser avaliado mensalmente e seus pontos acumulam para a distribuição trimestral.
          </P>
        </Section>

        {/* Pontuação */}
        <Section title="Pontuação por Atitude">
          <P>
            Todo mês, cada participante recebe <Highlight>1 avaliação</Highlight> que identifica suas atitudes positivas e negativas.
            A pontuação segue uma escala fixa:
          </P>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Positivas */}
            <div className="rounded-card border border-green-200 dark:border-green-800 p-4">
              <h3 className="font-semibold text-green-700 dark:text-green-400 mb-3">Atitudes Positivas</h3>
              <div className="space-y-0 divide-y divide-green-100 dark:divide-green-900">
                {[
                  ['Executor', '+2', 'Cumpre suas tarefas com consistência. Entrega o que foi combinado, no prazo, sem precisar de cobranças. Faz o básico bem feito, todos os dias.'],
                  ['Responsável', '+4', 'Vai além da sua função. Assume a responsabilidade pelo resultado final, não apenas pela tarefa. Quando algo dá errado, age para resolver em vez de apontar culpados.'],
                  ['Multiplicador', '+6', 'Ensina, treina e desenvolve outras pessoas. Compartilha conhecimento de forma ativa, documenta processos e eleva o nível do time ao seu redor.'],
                  ['Construtor', '+9', 'Cria sistemas, processos e estruturas que funcionam sem ele. Pensa no longo prazo e constrói soluções escaláveis que beneficiam toda a empresa.'],
                  ['Fundador', '+12', 'Age como dono do negócio. Toma decisões pensando no todo, assume riscos calculados, protege a cultura e o resultado da empresa como se fosse sua.'],
                ].map(([name, pts, desc]) => (
                  <AttitudeRow key={name} name={name} pts={pts} desc={desc} color="green" />
                ))}
              </div>
            </div>

            {/* Negativas */}
            <div className="rounded-card border border-red-200 dark:border-red-800 p-4">
              <h3 className="font-semibold text-red-700 dark:text-red-400 mb-3">Atitudes Negativas</h3>
              <div className="space-y-0 divide-y divide-red-100 dark:divide-red-900">
                {[
                  ['Passividade', '-2', 'Não toma iniciativa. Espera que outros resolvam os problemas ou digam o que fazer. Tem capacidade de agir mas escolhe não agir.'],
                  ['Terceirizador', '-4', 'Transfere suas responsabilidades para outros. Joga tarefas, problemas e decisões que são suas para colegas ou lideranças, sem justificativa.'],
                  ['Isolador', '-6', 'Retém informação e trabalha sozinho de forma prejudicial. Não compartilha conhecimento, cria silos e dificulta a colaboração do time.'],
                  ['Destruidor de Sistema', '-9', 'Sabota processos, ignora regras estabelecidas ou desfaz o trabalho de outros. Suas ações enfraquecem a cultura e os sistemas da empresa.'],
                  ['Antifundador', '-12', 'Age ativamente contra os interesses do negócio. Prejudica a empresa de forma consciente, seja por negligência grave ou por ação deliberada. Penalidade irreversível.'],
                ].map(([name, pts, desc]) => (
                  <AttitudeRow key={name} name={name} pts={pts} desc={desc} color="red" />
                ))}
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-btn p-4">
            <h4 className="font-semibold text-yellow-800 dark:text-yellow-300 mb-2">Regra do Antifundador</h4>
            <P>
              O Antifundador é a penalidade mais grave. Quando aplicado, <Highlight>zera TODOS os pontos do trimestre corrente</Highlight> e
              o colaborador é <Highlight>removido permanentemente</Highlight> do programa Ownership. Esta ação é irreversível.
            </P>
          </div>
        </Section>

        {/* Como funciona a avaliação */}
        <Section title="Como funciona a avaliação mensal">
          <P>
            A cada mês, a avaliação identifica quais atitudes o colaborador demonstrou:
          </P>
          <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 mb-3">
            <li>Apenas a <Highlight>maior atitude positiva</Highlight> conta (não somam entre si)</li>
            <li><Highlight>Todas as atitudes negativas</Highlight> identificadas são subtraídas (somam)</li>
            <li>Pontuação do mês = maior positiva + soma das negativas</li>
            <li>Mês sem avaliação = <Highlight>0 pontos</Highlight> (não é ignorado na média)</li>
          </ul>

          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-btn p-4 text-sm">
            <p className="font-medium text-gray-900 dark:text-white mb-2">Exemplo:</p>
            <p className="text-gray-700 dark:text-gray-300">
              Colaborador marcou Sim para Fundador (+12) e Sim para Passividade (-2).
              <br />Pontuação do mês: <Highlight>12 + (-2) = 10 pontos</Highlight>
            </p>
          </div>
        </Section>

        {/* Média trimestral */}
        <Section title="Média Trimestral">
          <P>
            A cada trimestre, o sistema calcula a média dos 3 meses:
          </P>

          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-btn p-4 mb-4 font-mono text-sm">
            <p className="text-gray-700 dark:text-gray-300">Média = arredondamento(soma dos 3 meses / 3)</p>
            <p className="text-gray-500 mt-1">Arredondamento: 0.5 ou mais sobe</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-btn p-3 text-sm">
              <p className="text-gray-500">Exemplo 1:</p>
              <p className="text-gray-700 dark:text-gray-300">(12 + 0 + 9) / 3 = 7.0 → <Highlight>7 pts</Highlight></p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-btn p-3 text-sm">
              <p className="text-gray-500">Exemplo 2:</p>
              <p className="text-gray-700 dark:text-gray-300">(12 + 12 + 2) / 3 = 8.67 → <Highlight>9 pts</Highlight></p>
            </div>
          </div>

          <P>Os trimestres do ano são:</P>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-center">
            {[
              ['Q1', 'Jan, Fev, Mar'],
              ['Q2', 'Abr, Mai, Jun'],
              ['Q3', 'Jul, Ago, Set'],
              ['Q4', 'Out, Nov, Dez'],
            ].map(([q, months]) => (
              <div key={q} className="bg-primary/10 dark:bg-primary/20 rounded-btn p-2">
                <span className="font-bold text-primary">{q}</span>
                <br />
                <span className="text-gray-600 dark:text-gray-400">{months}</span>
              </div>
            ))}
          </div>
        </Section>

        {/* Acumulado e Decay */}
        <Section title="Pontos Acumulados e Ciclagem Anual">
          <P>
            A participação nos resultados é baseada no <Highlight>total acumulado de pontos</Highlight>, não apenas no trimestre atual.
            Quanto mais tempo contribuindo com atitudes positivas, maior a participação.
          </P>
          <P>
            Porém, para evitar que colaboradores antigos acumulem vantagem permanente, existe a <Highlight>ciclagem anual</Highlight>:
          </P>
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-btn p-4 mb-4 font-mono text-sm">
            <p className="text-gray-700 dark:text-gray-300">pontos_ano_atual = (pontos_ano_anterior x 0.5) + pontos_conquistados_ano_atual</p>
          </div>
          <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2">
            <li><Highlight>50% de decay</Highlight> nos pontos do ano anterior</li>
            <li>Aplicado uma vez por ano pelo administrador</li>
            <li>O sistema mostra uma prévia antes de aplicar</li>
            <li>Operação <Highlight>irreversível</Highlight> — um snapshot é salvo para auditoria</li>
            <li>Apenas o ano imediatamente anterior decai (anos mais antigos já foram processados)</li>
          </ul>
        </Section>

        {/* Legacy */}
        <Section title="Legacy — Reconhecimento por Tempo de Grupo">
          <P>
            O Legacy é a regra de reconhecimento para quem permaneceu no grupo e contribuiu ao longo dos anos.
            <Highlight> Não é sociedade</Highlight>, mas é uma forma de reconhecer a trajetória da pessoa mesmo após sua saída.
          </P>
          <P>
            Quando um colaborador elegível ao Ownership deixa o grupo, sua última pontuação é <Highlight>congelada</Highlight> e
            ele continua recebendo a distribuição por um período proporcional ao tempo de contribuição:
          </P>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            {[
              ['10+ anos', '1 ano', 'de distribuição após a saída'],
              ['20+ anos', '2 anos', 'de distribuição após a saída'],
              ['30+ anos', '3 anos', 'de distribuição após a saída'],
            ].map(([tempo, beneficio, desc]) => (
              <div key={tempo} className="bg-gray-50 dark:bg-gray-900/50 rounded-card border border-gray-200 dark:border-gray-700 p-4 text-center">
                <p className="text-sm text-gray-500">{tempo} no grupo</p>
                <p className="text-2xl font-bold text-primary mt-1">{beneficio}</p>
                <p className="text-xs text-gray-500 mt-1">{desc}</p>
              </div>
            ))}
          </div>

          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Como funciona</h3>
          <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 mb-4">
            <li>Ao sair, a <Highlight>última pontuação acumulada é congelada</Highlight> — não recebe novas avaliações</li>
            <li>Com a pontuação congelada, continua participando da distribuição junto aos demais colaboradores ativos</li>
            <li>A cada ano de Legacy, a pontuação congelada <Highlight>sofre ciclagem normalmente</Highlight> (50% de decay)</li>
            <li>O Legacy encerra no <Highlight>último trimestre completo</Highlight> dentro do período — se o prazo vencer em Janeiro, a pessoa ainda recebe pelo Q1 daquele ano</li>
            <li>Após o último trimestre válido, o colaborador deixa de receber definitivamente</li>
          </ul>

          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-btn p-4 text-sm mb-4">
            <p className="font-medium text-gray-900 dark:text-white mb-2">Exemplo:</p>
            <p className="text-gray-700 dark:text-gray-300">
              Maria trabalhou 12 anos no grupo e está com 38 pontos no momento da saída.
              Tem direito a <Highlight>1 ano de Legacy</Highlight>.
              <br />Durante esse ano, participa da distribuição com seus 38 pontos congelados.
              <br />Ao final do ano, a ciclagem reduz para 19 pontos — mas o Legacy já encerrou, então Maria deixa de receber.
            </p>
            <hr className="my-3 border-gray-200 dark:border-gray-700" />
            <p className="text-gray-700 dark:text-gray-300">
              Joaquim trabalhou 30 anos no grupo e está com 52 pontos no momento da saída.
              Tem direito a <Highlight>3 anos de Legacy</Highlight>.
              <br />Ano 1: recebe com 52 pontos → ciclagem reduz para 26.
              <br />Ano 2: recebe com 26 pontos → ciclagem reduz para 13.
              <br />Ano 3: recebe com 13 pontos → ao final, o Legacy encerra e Joaquim deixa de receber.
            </p>
          </div>

          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-btn p-3 text-sm space-y-2">
            <p className="text-gray-700 dark:text-gray-300">
              <Highlight>Importante:</Highlight> o Legacy é um benefício exclusivo para quem se aposentou ou encerrou sua jornada de forma reconhecida pelo grupo.
              Pessoas que saíram do grupo, independente do tempo de casa ou do motivo, <Highlight>não possuem direito ao Legacy nem a qualquer distribuição futura</Highlight>.
            </p>
            <p className="text-gray-700 dark:text-gray-300">
              <Highlight>Retorno ao grupo:</Highlight> quem saiu e voltou a trabalhar no grupo está sujeito às regras como se fosse um funcionário novo —
              pontuação começa do zero e o período de <Highlight>1 ano de elegibilidade recomeça</Highlight> até poder participar do Ownership novamente.
            </p>
          </div>
        </Section>

        {/* Transferência entre empresas */}
        <Section title="Transferência entre Empresas">
          <P>
            Um colaborador pode ser transferido entre empresas do grupo. As regras dependem do tipo de movimentação:
          </P>

          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Transferência Voluntária</h3>
          <P>
            Quando o colaborador solicita a mudança por vontade própria:
          </P>
          <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 mb-4">
            <li>A pontuação fica <Highlight>congelada por 2 trimestres consecutivos</Highlight> na empresa de origem</li>
            <li>Durante o congelamento, a habilitação para recebimento segue as <Highlight>regras de elegibilidade da empresa anterior</Highlight> — se a empresa estiver apta a distribuir, o colaborador recebe com base na pontuação congelada</li>
            <li>Após os 2 trimestres, o colaborador inicia na nova empresa com seus pontos e volta a ser avaliado normalmente</li>
            <li>A transferência deve ser registrada formalmente</li>
          </ul>

          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-btn p-3 mb-4 text-sm">
            <p className="text-gray-700 dark:text-gray-300">
              <Highlight>Exemplo:</Highlight> se a transferência voluntária ocorre em Março (Q1), o colaborador fica congelado durante Q2 e Q3.
              Se a empresa de origem estiver elegível para distribuição nesses trimestres (atingindo os gatilhos de faturamento e margem operacional),
              o colaborador recebe com base na pontuação congelada. Caso contrário, não recebe.
              A partir de Q4, o colaborador passa a ser avaliado e receber pela nova empresa.
            </p>
          </div>

          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Convocação (Transferência pela Empresa)</h3>
          <P>
            Quando a empresa solicita a transferência do colaborador por necessidade do negócio:
          </P>
          <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 mb-4">
            <li>Os pontos acumulados são <Highlight>mantidos integralmente</Highlight></li>
            <li>O colaborador passa a <Highlight>receber pela nova empresa</Highlight> imediatamente, sem interrupção</li>
            <li>As avaliações continuam normalmente, sem período de congelamento</li>
            <li>A convocação deve ser registrada e aprovada pela administração</li>
          </ul>
        </Section>

        {/* Incubação de Startups */}
        <Section title="Período de Incubação — Startups">
          <P>
            Empresas classificadas como <Highlight>Startup</Highlight> possuem um período de incubação de <Highlight>18 meses</Highlight> antes
            de serem obrigadas a distribuir lucros pelo Ownership.
          </P>
          <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 mb-4">
            <li>Durante os 18 meses, se a Startup atingir <Highlight>margem operacional acima de 30%</Highlight>, já distribui normalmente</li>
            <li>Mesmo com a Startup inativa para distribuição, os <Highlight>colaboradores continuam sendo avaliados, acumulando pontos e recebendo de acordo com sua pontuação</Highlight> — o período de incubação protege a empresa, não penaliza os funcionários</li>
            <li>Se a Startup <Highlight>graduar para Tração</Highlight> antes dos 18 meses (MRR {'>='} R$50k por 3 meses consecutivos), o período de incubação encerra imediatamente e passam a valer as regras de Tração</li>
            <li>O período de incubação conta a partir da <Highlight>data de cadastro da empresa</Highlight> no sistema</li>
          </ul>

          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-btn p-4 mb-4">
            <h4 className="font-semibold text-red-800 dark:text-red-300 mb-2">Após os 18 meses</h4>
            <P>
              Encerrado o período de incubação, a Startup passa a seguir as regras normais de elegibilidade.
              Se a empresa <Highlight>não gerar lucro ou não alcançar a margem operacional mínima de 30%</Highlight>,
              a distribuição fica inativa e os funcionários daquela empresa <Highlight>não recebem</Highlight> naquele período.
              A cada trimestre a elegibilidade é reavaliada do zero.
            </P>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-btn p-4">
            <h4 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">Por que 18 meses?</h4>
            <P>
              Startups precisam de tempo para encontrar seu modelo de negócio e atingir sustentabilidade.
              O período de incubação permite que a empresa se estabeleça antes de assumir o compromisso de distribuição,
              sem penalizar os colaboradores que acumulam pontos normalmente durante esse período.
            </P>
          </div>
        </Section>

        {/* Distribuição */}
        <Section title="Distribuição de Resultados">
          <P>
            A distribuição é <Highlight>trimestral</Highlight> e depende de dois fatores: a elegibilidade da empresa e a pontuação individual.
          </P>

          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">1. Pool de Distribuição</h3>
          <P>
            Cada empresa destina um percentual do seu <Highlight>lucro distribuível</Highlight> (lucro líquido final da empresa) para o pool do Ownership,
            conforme seu estágio de maturidade:
          </P>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6 text-center">
            {[
              ['Startup', '30%', 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800', 'text-blue-700 dark:text-blue-400'],
              ['Tração', '25%', 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800', 'text-yellow-700 dark:text-yellow-400'],
              ['Growth', '22%', 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800', 'text-purple-700 dark:text-purple-400'],
              ['Scale', '20%', 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800', 'text-orange-700 dark:text-orange-400'],
              ['Madura', '18%', 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800', 'text-green-700 dark:text-green-400'],
            ].map(([stage, pct, bg, color]) => (
              <div key={stage} className={`rounded-card border p-4 ${bg}`}>
                <p className={`text-sm font-medium ${color}`}>{stage}</p>
                <p className={`text-2xl font-bold ${color}`}>{pct}</p>
                <p className="text-xs text-gray-500">do lucro distribuível</p>
              </div>
            ))}
          </div>

          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">2. Elegibilidade da Empresa</h3>
          <P>
            Antes de distribuir, o sistema verifica se a empresa atingiu os gatilhos mínimos no trimestre:
          </P>
          <div className="overflow-x-auto mb-4">
            <table className="w-full text-sm border border-gray-200 dark:border-gray-700 rounded-btn overflow-hidden">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-900/50">
                  <th className="text-left px-4 py-2 font-medium text-gray-500">Fase</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-500">Mínimo Mensal</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-500">Margem Mínima</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-500">Pool</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-500">Promoção</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-500">Rebaixamento</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                <tr>
                  <td className="px-4 py-2 font-medium text-gray-900 dark:text-white">Startup</td>
                  <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">Não exigido</td>
                  <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">30%</td>
                  <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">30%</td>
                  <td className="px-4 py-2 text-gray-700 dark:text-gray-300">MRR {'>='} R$ 50k por 3 meses consecutivos</td>
                  <td className="px-4 py-2 text-gray-500">— (piso)</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 font-medium text-gray-900 dark:text-white">Tração</td>
                  <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">R$ 60k</td>
                  <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">30%</td>
                  <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">25%</td>
                  <td className="px-4 py-2 text-gray-700 dark:text-gray-300">MRR {'>='} R$ 120k por 6 meses consecutivos</td>
                  <td className="px-4 py-2 text-gray-700 dark:text-gray-300">MRR {'<'} R$ 60k por 6 meses consecutivos</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 font-medium text-gray-900 dark:text-white">Growth</td>
                  <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">R$ 150k</td>
                  <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">30%</td>
                  <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">22%</td>
                  <td className="px-4 py-2 text-gray-700 dark:text-gray-300">MRR {'>='} R$ 1M por 12 meses consecutivos</td>
                  <td className="px-4 py-2 text-gray-700 dark:text-gray-300">MRR {'<'} R$ 150k por 6 meses consecutivos</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 font-medium text-gray-900 dark:text-white">Scale</td>
                  <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">R$ 1M</td>
                  <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">35%</td>
                  <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">20%</td>
                  <td className="px-4 py-2 text-gray-700 dark:text-gray-300">MRR {'>='} R$ 10M por 12 meses + NRR {'>='} 120%</td>
                  <td className="px-4 py-2 text-gray-700 dark:text-gray-300">MRR {'<'} R$ 1M por 6 meses consecutivos <strong>ou</strong> margem {'<'} 30% por 6 meses</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 font-medium text-gray-900 dark:text-white">Madura</td>
                  <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">R$ 10M</td>
                  <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">35%</td>
                  <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">18%</td>
                  <td className="px-4 py-2 text-gray-500">— (topo)</td>
                  <td className="px-4 py-2 text-gray-700 dark:text-gray-300">MRR {'<'} R$ 10M por 6 meses consecutivos <strong>ou</strong> margem {'<'} 30% por 6 meses</td>
                </tr>
              </tbody>
            </table>
          </div>
          <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 mb-3">
            <li>A média dos 3 meses do trimestre precisa atingir <Highlight>simultaneamente</Highlight> o faturamento e a lucratividade mínima</li>
            <li>Se não atingir, a distribuição fica <Highlight>inativa</Highlight> naquele trimestre</li>
            <li>A inatividade <Highlight>não é cumulativa</Highlight> — o trimestre seguinte começa do zero</li>
            <li>A <Highlight>promoção</Highlight> avança a empresa para a próxima fase; o <Highlight>rebaixamento</Highlight> volta para a fase anterior quando os mínimos não são sustentados</li>
          </ul>

          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">3. Cálculo Individual</h3>
          <P>
            Quando a empresa é elegível, o valor do pool é distribuído proporcionalmente ao acumulado de pontos de cada participante:
          </P>
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-btn p-4 font-mono text-sm">
            <p className="text-gray-700 dark:text-gray-300">participação = meus_pontos_acumulados / total_pontos_todos_participantes</p>
            <p className="text-gray-700 dark:text-gray-300 mt-1">meu_valor = lucro_distribuível_da_empresa x percentual_pool x participação</p>
          </div>
        </Section>

        {/* Transparência */}
        <Section title="Transparência">
          <P>
            O Ownership opera com <Highlight>transparência total</Highlight>. Todos os participantes podem ver:
          </P>
          <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2">
            <li>Faturamento e lucratividade de todas as empresas do grupo</li>
            <li>Status de elegibilidade para distribuição em cada trimestre</li>
            <li>Ranking de pontos de todos os participantes</li>
            <li>Seu percentual de participação no pool</li>
            <li>Histórico completo de avaliações e ciclagens</li>
          </ul>
        </Section>

        {/* Resumo */}
        <div className="bg-primary/5 dark:bg-primary/10 border border-primary/20 rounded-card p-6 text-center">
          <p className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Atitude gera ponto. Ponto gera participação no resultado.
          </p>
          <p className="text-gray-600 dark:text-gray-400">
            Ownership — Programa de Participação nos Resultados e Liderança — Grupo Jonnpo
          </p>
        </div>
      </div>
    </div>
  )
}

export default OwnershipRulesPage
